import React, { forwardRef, useMemo } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Fade,
  Grid,
  Grow,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  WhatsApp,
  Facebook,
  Article as ArticleIcon,
  ArrowForward,
  CheckCircle,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import defaultAvatar from "../../assets/images/default.png";
import { ModernTextField } from "./styledComponents";

const Bienvenida = forwardRef((props, ref) => {
  const { formValues, onChange, handleNext, dataNutriologoSubdominio } = props;
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

  const nutriologoInfo = dataNutriologoSubdominio?.nutriologoInfo || {};
  const brandPrimary = nutriologoInfo.color1 || theme.palette.primary.main;
  const brandSecondary = nutriologoInfo.color2 || theme.palette.secondary.main;
  const accentColor = nutriologoInfo.color3 || "#22d3ee";
  const primaryGradient = `linear-gradient(135deg, ${brandPrimary}, ${brandSecondary})`;
  const glassOverlay = alpha("#ffffff", 0.08);

  const avatarSrc = useMemo(() => {
    const image = nutriologoInfo?.usuario?.imagen;
    if (
      !image ||
      image === "" ||
      image === "default" ||
      image === "default.png"
    ) {
      return defaultAvatar;
    }
    return image;
  }, [nutriologoInfo]);

  const welcomeTitle = nutriologoInfo?.nombreAMostrar
    ? nutriologoInfo.nombreAMostrar
    : `${nutriologoInfo?.usuario?.nombre || "Tu"} ${
        nutriologoInfo?.usuario?.apellidos || "nutriólogo"
      }`.trim();
  const occupationLabel = (
    nutriologoInfo?.giro || "Asesor nutricional"
  ).toUpperCase();
  const displayName = nutriologoInfo?.nombreAMostrar?.trim()
    ? nutriologoInfo.nombreAMostrar
    : `${nutriologoInfo?.usuario?.nombre || ""} ${
        nutriologoInfo?.usuario?.apellidos || ""
      }`.trim() || "tu especialista";
  const highlightedSignature = displayName;

  const heroHighlights = [
    nutriologoInfo?.especialidad && {
      label: "Especialidad",
      value: nutriologoInfo.especialidad,
    },
    nutriologoInfo?.modalidadConsulta && {
      label: "Modalidad",
      value: nutriologoInfo.modalidadConsulta,
    },
    typeof nutriologoInfo?.aniosExperiencia === "number" && {
      label: "Experiencia",
      value: `${nutriologoInfo.aniosExperiencia} años de práctica`,
    },
  ].filter(Boolean);

  const steps = [
    "Completa tus datos de contacto",
    "Comparte tu información de salud",
    "Accede a tu panel personalizado",
  ];

  const openLink = (url, options = { newTab: true }) => {
    if (!url) return;
    if (options.newTab) {
      window.open(url, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = url;
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    onChange(name, value);
  };

  const allowOnlyNumbers = (event) => {
    if (!/[0-9]/.test(event.key)) {
      event.preventDefault();
    }
  };

  const showContactActions =
    Boolean(nutriologoInfo?.telefonoAMostrar) ||
    Boolean(nutriologoInfo?.urlFacebook);
  const publicProfileUrl = nutriologoInfo?.subdomain
    ? `https://${nutriologoInfo.subdomain}.nutrilander.com/perfil`
    : "https://nutrilander.com";

  return (
    <Box
      ref={ref}
      sx={{
        minHeight: "100vh",
        background: `radial-gradient(circle at top, ${alpha(
          brandPrimary,
          0.12
        )}, ${alpha(brandSecondary, 0.05)} 45%, ${
          theme.palette.background.default
        })`,
        py: { xs: 4, sm: 6 },
        px: { xs: 2, sm: 3, md: 5 },
      }}
    >
      <Grid
        container
        spacing={{ xs: 4, md: 5 }}
        alignItems="stretch"
        flexDirection={{ xs: "column", md: "row" }}
      >
        <Grid item xs={12} md={6}>
          <Grow in timeout={850}>
            <Box
              sx={{
                position: "relative",
                borderRadius: { xs: 4, md: 5 },
                overflow: "hidden",
                background: primaryGradient,
                color: "#fff",
                height: "100%",
                minHeight: { xs: 520, md: "100%" },
                p: { xs: 4, sm: 5 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                boxShadow: `0 40px 90px ${alpha(brandPrimary, 0.35)}`,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background: `radial-gradient(circle at 20% 20%, ${alpha(
                    "#ffffff",
                    0.25
                  )}, transparent 45%)`,
                }}
              />

              <Stack spacing={3} sx={{ position: "relative" }}>
                <Stack direction="row" spacing={2.5} alignItems="center">
                  <Avatar
                    src={avatarSrc}
                    alt="Nutriólogo"
                    sx={{
                      width: 88,
                      height: 88,
                      border: `3px solid ${glassOverlay}`,
                      boxShadow: `0 12px 32px ${alpha("#000", 0.25)}`,
                    }}
                  />
                  <Box>
                    <Typography variant="overline" sx={{ letterSpacing: 3 }}>
                      {occupationLabel}
                    </Typography>
                    <Typography variant="h4" fontWeight={800} lineHeight={1.15}>
                      {welcomeTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      {nutriologoInfo?.textoBienvenidaAPacientes ||
                        "Creamos experiencias de nutrición enfocadas en tus objetivos para que puedas sentirte mejor todos los días."}
                    </Typography>
                  </Box>
                </Stack>

                <Box>
                  <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                    {nutriologoInfo?.textoSegundoBienvenidaAPacientes ||
                      "Acompañamiento continuo, control avanzado y seguimiento en tiempo real."}
                  </Typography>
                  {heroHighlights.length ? (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                        },
                        gap: 2,
                        mt: 3,
                      }}
                    >
                      {heroHighlights.map((item) => (
                        <Paper
                          key={item.label}
                          elevation={0}
                          sx={{
                            p: 2,
                            borderRadius: 3,
                            backgroundColor: alpha("#000", 0.15),
                            backdropFilter: "blur(12px)",
                            color: "inherit",
                          }}
                        >
                          <Typography variant="caption" sx={{ opacity: 0.7 }}>
                            {item.label}
                          </Typography>
                          <Typography variant="h6" fontWeight={700}>
                            {item.value}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  ) : (
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 3,
                        p: 3,
                        borderRadius: 3,
                        backgroundColor: alpha("#000", 0.18),
                        backdropFilter: "blur(14px)",
                        color: "inherit",
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ opacity: 0.85 }}>
                        Un espacio hecho para ti
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.85, mt: 1 }}>
                        Tu especialista compartirá los detalles completos de tu
                        plan durante el registro. Mientras tanto, puedes avanzar
                        con tus datos o usar los canales de contacto para
                        resolver dudas.
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" mt={2}>
                        {[
                          "Atención cercana",
                          "Plan a tu medida",
                          "Seguimiento constante",
                        ].map((highlight) => (
                          <Chip
                            key={highlight}
                            label={highlight}
                            variant="outlined"
                            sx={{
                              borderColor: alpha("#fff", 0.5),
                              color: "inherit",
                              borderRadius: 999,
                            }}
                          />
                        ))}
                      </Stack>
                    </Paper>
                  )}
                </Box>
              </Stack>

              <Stack spacing={2.5} sx={{ position: "relative" }}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  {showContactActions && (
                    <>
                      {nutriologoInfo?.telefonoAMostrar && (
                        <Tooltip title="Escríbenos por WhatsApp">
                          <IconButton
                            size="large"
                            sx={{
                              backgroundColor: alpha("#fff", 0.18),
                              color: "#fff",
                              borderRadius: 2,
                              border: `1px solid ${glassOverlay}`,
                              "&:hover": {
                                backgroundColor: alpha("#fff", 0.3),
                              },
                            }}
                            onClick={() =>
                              openLink(
                                `https://wa.me/52${nutriologoInfo.telefonoAMostrar}`
                              )
                            }
                          >
                            <WhatsApp />
                          </IconButton>
                        </Tooltip>
                      )}
                      {nutriologoInfo?.urlFacebook && (
                        <Tooltip title="Visítanos en Facebook">
                          <IconButton
                            size="large"
                            sx={{
                              backgroundColor: alpha("#fff", 0.18),
                              color: "#fff",
                              borderRadius: 2,
                              border: `1px solid ${glassOverlay}`,
                              "&:hover": {
                                backgroundColor: alpha("#fff", 0.3),
                              },
                            }}
                            onClick={() =>
                              openLink(
                                nutriologoInfo.urlFacebook.startsWith("http")
                                  ? nutriologoInfo.urlFacebook
                                  : `https://${nutriologoInfo.urlFacebook}`
                              )
                            }
                          >
                            <Facebook />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  <Button
                    variant="contained"
                    color="inherit"
                    startIcon={<ArticleIcon />}
                    onClick={() =>
                      openLink(publicProfileUrl, { newTab: false })
                    }
                    sx={{
                      color: brandPrimary,
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: 2,
                    }}
                  >
                    Ir al perfil público
                  </Button>
                </Stack>

                {(nutriologoInfo?.subdomain ||
                  nutriologoInfo?.telefonoAMostrar ||
                  nutriologoInfo?.direccionAMostrar) && (
                  <Box>
                    <Typography variant="body2" sx={{ opacity: 0.8 }}>
                      {nutriologoInfo?.subdomain && (
                        <>
                          {nutriologoInfo.subdomain}.nutrilander.com
                          <br />
                        </>
                      )}
                      {nutriologoInfo?.telefonoAMostrar && (
                        <>
                          Teléfono: {nutriologoInfo.telefonoAMostrar}
                          <br />
                        </>
                      )}
                      {nutriologoInfo?.direccionAMostrar && (
                        <>Dirección: {nutriologoInfo.direccionAMostrar}</>
                      )}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Grow>
        </Grid>

        <Grid item xs={12} md={6}>
          <Fade in timeout={900}>
            <Paper
              elevation={0}
              sx={{
                height: "100%",
                p: { xs: 3, sm: 4 },
                borderRadius: { xs: 4, md: 5 },
                boxShadow: "0 25px 80px rgba(15,23,42,0.12)",
                border: `1px solid ${alpha(brandPrimary, 0.1)}`,
                background: theme.palette.background.paper,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  background: `linear-gradient(180deg, ${alpha(
                    brandSecondary,
                    0.08
                  )}, transparent 40%)`,
                }}
              />
              <Stack spacing={3} sx={{ position: "relative" }}>
                <Box>
                  <Typography
                    variant="overline"
                    sx={{ color: accentColor, letterSpacing: 3 }}
                  >
                    Regístrate en minutos
                  </Typography>
                  <Typography variant="h4" fontWeight={800} gutterBottom>
                    Crea tu acceso personalizado
                  </Typography>
                  <Typography color="text.secondary">
                    Conecta tus datos con el consultorio digital de{" "}
                    <Box
                      component="span"
                      sx={{ color: brandPrimary, fontWeight: 700, mx: 0.5 }}
                    >
                      {highlightedSignature}
                    </Box>
                    y recibe recordatorios, planes y seguimiento en un mismo
                    lugar.
                  </Typography>
                </Box>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    ¿Ya tienes cuenta?
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/login"
                    variant="outlined"
                    color="primary"
                    sx={{ px: 3, borderRadius: 999 }}
                  >
                    Ir al inicio de sesión
                  </Button>
                </Stack>

                <Stack spacing={1.5}>
                  {steps.map((step) => (
                    <Stack
                      key={step}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                    >
                      <Chip
                        icon={<CheckCircle fontSize="small" />}
                        label={step}
                        color="primary"
                        variant="outlined"
                        sx={{
                          borderRadius: 999,
                          borderColor: alpha(brandPrimary, 0.2),
                          backgroundColor: alpha(brandPrimary, 0.05),
                        }}
                      />
                    </Stack>
                  ))}
                </Stack>

                <Stack spacing={2.5}>
                  <ModernTextField
                    label="Número de teléfono"
                    name="telefono"
                    value={formValues.telefono || ""}
                    inputProps={{ maxLength: 10 }}
                    onChange={handleInputChange}
                    onKeyPress={allowOnlyNumbers}
                    variant="outlined"
                    fullWidth
                  />

                  <ModernTextField
                    label="Contraseña"
                    name="password"
                    type="password"
                    value={formValues.password || ""}
                    onChange={handleInputChange}
                    variant="outlined"
                    fullWidth
                  />

                  <ModernTextField
                    label="Confirmar contraseña"
                    name="confirmPassword"
                    type="password"
                    value={formValues.confirmPassword || ""}
                    onChange={handleInputChange}
                    variant="outlined"
                    fullWidth
                  />
                </Stack>

                <Stack spacing={1}>
                  <Button
                    fullWidth
                    size="large"
                    variant="contained"
                    endIcon={<ArrowForward />}
                    onClick={handleNext}
                    sx={{
                      py: 1.4,
                      borderRadius: 999,
                      textTransform: "none",
                      fontSize: "1rem",
                      fontWeight: 700,
                      background: primaryGradient,
                      boxShadow: `0 18px 45px ${alpha(brandPrimary, 0.35)}`,
                      "&:hover": {
                        background: primaryGradient,
                        opacity: 0.95,
                      },
                    }}
                  >
                    Continuar registro
                  </Button>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    align="center"
                  >
                    Tus datos están protegidos y podrás editarlos después.
                  </Typography>
                </Stack>

                {showContactActions && (
                  <>
                    <Divider textAlign="left">Contacta a tu asesor</Divider>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      {nutriologoInfo?.telefonoAMostrar && (
                        <Button
                          startIcon={<WhatsApp />}
                          variant="outlined"
                          color="success"
                          sx={{ borderRadius: 2 }}
                          onClick={() =>
                            openLink(
                              `https://wa.me/52${nutriologoInfo.telefonoAMostrar}`
                            )
                          }
                        >
                          WhatsApp
                        </Button>
                      )}
                      {nutriologoInfo?.urlFacebook && (
                        <Button
                          startIcon={<Facebook />}
                          variant="outlined"
                          color="primary"
                          sx={{ borderRadius: 2 }}
                          onClick={() =>
                            openLink(
                              nutriologoInfo.urlFacebook.startsWith("http")
                                ? nutriologoInfo.urlFacebook
                                : `https://${nutriologoInfo.urlFacebook}`
                            )
                          }
                        >
                          Facebook
                        </Button>
                      )}
                    </Stack>
                  </>
                )}
              </Stack>
            </Paper>
          </Fade>
        </Grid>
      </Grid>
    </Box>
  );
});

export default Bienvenida;
